/**
 * Kesiapan absen masuk — cermin aturan server, dipakai untuk MEMANDU, bukan untuk MENEGAKKAN.
 *
 * Server tetap satu-satunya yang memutuskan (`AttendanceService.performCheckIn`). Modul ini ada
 * karena tanpanya pengguna lapangan baru mengetahui masalahnya setelah menekan tombol dan
 * menunggu jaringan — lalu menerima penolakan yang tidak dapat ia perbaiki di layar itu.
 *
 * Yang dilakukan di sini hanyalah menjawab lebih awal: *"kalau dikirim sekarang, apa yang akan
 * terjadi, dan apa yang bisa saya perbaiki dulu?"* Menghapus modul ini tidak membuat seorang pun
 * dapat melakukan absen masuk yang seharusnya ditolak.
 *
 * Aturan di bawah SENGAJA dijaga sebangun dengan `location-evaluation.ts` dan
 * `attendance.service.ts` di API. Bila keduanya menyimpang, server yang benar.
 */

/** Hasil evaluasi lokasi. Nilai final SELALU milik server (ADR-0036 D3); ini hanya perkiraan. */
export type LocationVerification =
  'VERIFIED' | 'UNVERIFIED_NO_GPS' | 'UNVERIFIED_LOW_ACCURACY' | 'OUTSIDE_GEOFENCE';

export type WorkMode = 'WFO' | 'WFH' | 'FIELD_WORK' | 'BUSINESS_TRIP';

export type SelfiePolicy = 'REQUIRED' | 'OPTIONAL' | 'DISABLED';

/** Bagian kebijakan yang relevan bagi keputusan absen masuk. Sengaja subset, bukan seluruh policy. */
export interface PolicyForCheckIn {
  readonly minGpsAccuracyMeters: number;
  readonly selfiePolicy: SelfiePolicy;
  readonly allowCheckInOutsideGeofence: boolean;
  readonly outsideGeofenceRequiresReason: boolean;
  readonly fieldWorkGeofenceExempt: boolean;
}

export interface RawLocation {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracyMeters: number | null;
}

/** Lokasi kerja terdekat beserta jaraknya — dihitung server; klien hanya menerimanya. */
export interface NearestWorkLocation {
  readonly name: string;
  readonly distanceMeters: number;
  readonly geofenceRadiusMeters: number;
}

/**
 * Benar bila metode kerja menuntut evaluasi geofence (ADR-0034 B5).
 *
 * | Metode | Geofence dievaluasi? |
 * |---|---|
 * | `WFO` | selalu |
 * | `FIELD_WORK` | hanya bila kebijakan tidak membebaskannya |
 * | `WFH` / `BUSINESS_TRIP` | tidak pernah |
 */
export function geofenceAppliesTo(
  workMode: WorkMode,
  policy: Pick<PolicyForCheckIn, 'fieldWorkGeofenceExempt'>,
): boolean {
  if (workMode === 'WFO') return true;
  if (workMode === 'FIELD_WORK') return !policy.fieldWorkGeofenceExempt;
  return false;
}

/**
 * Perkiraan klasifikasi lokasi.
 *
 * Akurasi `null` diperlakukan sama dengan gagal ambang — tidak ada dasar membuktikan koordinat
 * memenuhi minimum kebijakan. Ini berbeda dari koordinat yang memang tidak ada sama sekali, dan
 * perbedaan itu penting: yang satu ditolak server, yang lain diterima dengan tanda.
 */
export function classifyLocation(
  location: RawLocation,
  policy: Pick<PolicyForCheckIn, 'minGpsAccuracyMeters'>,
  geofence: { readonly applies: boolean; readonly nearest: NearestWorkLocation | null },
): LocationVerification {
  if (location.latitude === null || location.longitude === null) {
    return 'UNVERIFIED_NO_GPS';
  }
  if (location.accuracyMeters === null || location.accuracyMeters > policy.minGpsAccuracyMeters) {
    return 'UNVERIFIED_LOW_ACCURACY';
  }
  if (!geofence.applies) {
    return 'VERIFIED';
  }
  if (!geofence.nearest) {
    // Tidak ada lokasi kerja aktif untuk dievaluasi — server memperlakukannya sama dengan
    // di luar geofence, bukan sebagai "lolos".
    return 'OUTSIDE_GEOFENCE';
  }
  return geofence.nearest.distanceMeters <= geofence.nearest.geofenceRadiusMeters
    ? 'VERIFIED'
    : 'OUTSIDE_GEOFENCE';
}

/**
 * Kesiapan mengirim absen masuk.
 *
 * - `ready` — server diperkirakan menerima tanpa catatan.
 * - `warn` — server diperkirakan MENERIMA, tetapi hasilnya tidak terverifikasi. Pengguna wajib
 *   diberi tahu konsekuensinya sebelum melanjutkan, bukan sesudahnya.
 * - `need` — ada yang harus diisi lebih dulu (alasan / selfie). Dapat diperbaiki di layar itu juga.
 * - `block` — server dipastikan menolak. Menampilkan tombol kirim di sini hanya membuat orang
 *   mengulang tindakan yang tidak akan pernah berhasil.
 */
export type CheckInReadiness =
  | { readonly kind: 'ready' }
  | { readonly kind: 'warn'; readonly message: string }
  | {
      readonly kind: 'need';
      readonly field: 'outsideRadiusReason' | 'selfie';
      readonly message: string;
    }
  | { readonly kind: 'block'; readonly message: string };

export interface CheckInDraft {
  readonly workMode: WorkMode;
  readonly location: RawLocation;
  readonly outsideRadiusReason: string;
  readonly selfieFileId: string | null;
  /** Jalan keluar kamera gagal (item 9, review 12 Sep 2026) — lihat `selfieSatisfied`. */
  readonly noSelfieReason: string;
}

/**
 * Benar bila syarat selfie kebijakan sudah terpenuhi — MODE-AGNOSTIK, dipakai `evaluateCheckIn`
 * (dalam rantai prioritas check-in) MAUPUN check-out langsung oleh `CheckPage` (K3, review
 * 12 Sep 2026: selfie berlaku sama untuk absen masuk dan pulang — TIDAK seperti aturan geofence
 * di modul ini yang sengaja hanya berlaku untuk check-in, ADR-0034 §5).
 *
 * `noSelfieReason` yang terisi (bukan hanya spasi) adalah jalan keluar SATU-SATUNYA saat kamera
 * gagal — mencerminkan `.trim()` server persis (`attendance.service.ts`).
 */
export function selfieSatisfied(
  policy: Pick<PolicyForCheckIn, 'selfiePolicy'>,
  selfieFileId: string | null,
  noSelfieReason: string,
): boolean {
  if (policy.selfiePolicy !== 'REQUIRED') return true;
  return selfieFileId !== null || noSelfieReason.trim() !== '';
}

/**
 * Apakah kolom "Alasan berada di luar radius" BERLAKU — terlepas dari sudah diisi atau belum.
 *
 * ADA KARENA cacat yang ditemukan 23 Agustus 2026 saat memakai aplikasi sebagai kurir sungguhan.
 * Layar Check In menentukan kemunculan kolom itu dari `evaluateCheckIn(...).kind === 'need'`.
 * Tetapi `evaluateCheckIn` adalah hasil VALIDASI: ia berhenti berkata `need` begitu alasannya
 * terisi. Akibatnya kolomnya menghilang setelah SATU karakter diketik — fokus ikut lenyap, dan
 * sisa kalimat mengetik ke ruang kosong.
 *
 * Yang tersimpan lalu "M", bukan "Mengantar barang ke klien". Dan alasan itu satu-satunya hal
 * yang dibaca penyelia untuk memutuskan menyetujui absen di luar radius — kebijakan bahkan
 * mewajibkannya. Mekanisme persetujuannya lumpuh tanpa satu pun galat.
 *
 * PENJAGANYA STRUKTURAL, bukan disiplin: parameternya `Pick<…, 'workMode' | 'location'>`, jadi
 * fungsi ini TIDAK DAPAT melihat isi alasan. Ia mustahil bergantung padanya lagi, dan siapa pun
 * yang mencoba menambahkannya harus mengubah tanda tangannya lebih dulu.
 *
 * Dipakai untuk MENAMPILKAN kolom. Yang menentukan boleh-tidaknya mengirim tetap
 * `evaluateCheckIn` — keberlakuan dan kelengkapan adalah dua pertanyaan berbeda, dan menyatukannya
 * persis yang melahirkan cacat ini.
 *
 * `serverFocusField` (PR #1330, 24 Sep 2026) — server MENANG atas perkiraan klien. Ditemukan
 * dari kurir Damha yang terjebak: GPS di gudang beratap logam sempat membaca posisi yang
 * perkiraan klien anggap masih di dalam radius, padahal server (sumber kebenaran, ADR-0036 D3)
 * sudah menolak dengan ATT-007 (`focusField: 'outsideRadiusReason'`). Tanpa parameter ini, kolom
 * satu-satunya yang bisa memperbaiki keadaan itu tidak pernah muncul. Field lain (mis. `'selfie'`)
 * TIDAK memicu kolom ini — hanya kode galat yang benar-benar menunjuk alasan luar-radius.
 */
export function outsideRadiusReasonApplies(
  draft: Pick<CheckInDraft, 'workMode' | 'location'>,
  policy: PolicyForCheckIn,
  nearest: NearestWorkLocation | null,
  serverFocusField?: 'outsideRadiusReason' | 'selfie' | null,
): boolean {
  if (serverFocusField === 'outsideRadiusReason') return true;
  if (!policy.outsideGeofenceRequiresReason) return false;
  // Tidak diizinkan sama sekali = DIBLOKIR, bukan diminta alasan. Menampilkan kolom di situ
  // menjanjikan jalan keluar yang tidak ada.
  if (!policy.allowCheckInOutsideGeofence) return false;
  const applies = geofenceAppliesTo(draft.workMode, policy);
  return classifyLocation(draft.location, policy, { applies, nearest }) === 'OUTSIDE_GEOFENCE';
}

/**
 * Urutan pemeriksaan mengikuti `performCheckIn` di server persis, karena urutan itulah yang
 * menentukan pesan mana yang dilihat pengguna ketika dua masalah terjadi bersamaan.
 */
export function evaluateCheckIn(
  draft: CheckInDraft,
  policy: PolicyForCheckIn,
  nearest: NearestWorkLocation | null,
): CheckInReadiness {
  const applies = geofenceAppliesTo(draft.workMode, policy);
  const verification = classifyLocation(draft.location, policy, { applies, nearest });

  if (verification === 'UNVERIFIED_LOW_ACCURACY') {
    return {
      kind: 'block',
      message:
        `Akurasi GPS belum memenuhi minimum kebijakan (${policy.minGpsAccuracyMeters} m). ` +
        'Keluar ke area terbuka atau tunggu sinyal membaik, lalu coba lagi.',
    };
  }

  if (verification === 'OUTSIDE_GEOFENCE') {
    if (!policy.allowCheckInOutsideGeofence) {
      return {
        kind: 'block',
        message: nearest
          ? `Anda ${Math.round(nearest.distanceMeters)} m dari ${nearest.name} ` +
            `(radius ${nearest.geofenceRadiusMeters} m), dan kebijakan organisasi tidak ` +
            'mengizinkan absen masuk di luar radius.'
          : 'Tidak ada lokasi kerja aktif yang cocok, dan kebijakan organisasi tidak ' +
            'mengizinkan absen masuk di luar radius.',
      };
    }
    if (policy.outsideGeofenceRequiresReason && draft.outsideRadiusReason.trim() === '') {
      return {
        kind: 'need',
        field: 'outsideRadiusReason',
        message: 'Anda di luar radius lokasi kerja. Tuliskan alasannya untuk melanjutkan.',
      };
    }
  }

  if (!selfieSatisfied(policy, draft.selfieFileId, draft.noSelfieReason)) {
    return {
      kind: 'need',
      field: 'selfie',
      message: 'Kebijakan organisasi mewajibkan swafoto saat absen masuk.',
    };
  }

  if (verification === 'UNVERIFIED_NO_GPS') {
    return {
      kind: 'warn',
      message:
        'Lokasi tidak terbaca. Absen masuk tetap dapat dikirim, tetapi akan tercatat TANPA ' +
        'verifikasi lokasi dan dapat ditanyakan manajer Anda.',
    };
  }

  if (verification === 'OUTSIDE_GEOFENCE') {
    return {
      kind: 'warn',
      message:
        'Absen masuk akan tercatat di luar radius lokasi kerja beserta alasan yang Anda tulis.',
    };
  }

  return { kind: 'ready' };
}

/** Boleh menekan tombol kirim? `warn` termasuk boleh — ia memberi tahu, bukan menghalangi. */
export function canSubmit(readiness: CheckInReadiness): boolean {
  return readiness.kind === 'ready' || readiness.kind === 'warn';
}

export interface GeoBoxState {
  readonly tone: 'neutral' | 'warn';
  /** `null` bila `tone === 'neutral'` — tidak ada apa pun untuk dikatakan tentang radius. */
  readonly message: string | null;
}

/**
 * Keadaan KARTU LOKASI itu sendiri (item 8, review 12 Sep 2026) — MENGGANTIKAN kotak terpisah
 * di bawah untuk kasus di luar radius. Kartu berubah kuning dan pesannya tampil DI DALAMNYA,
 * bukan di kotak kedua yang dulu dirender `ReadinessNotice`.
 *
 * SENGAJA fungsi TERPISAH dari `evaluateCheckIn`, bukan membaca `kind`/`field` hasilnya —
 * `evaluateCheckIn` adalah RANTAI PRIORITAS (akurasi → geofence-tidak-diizinkan →
 * perlu-alasan → perlu-selfie → tanpa-GPS → geofence-diizinkan), dan pesan di luar radius bisa
 * KALAH oleh pesan lain yang lebih prioritas (mis. swafoto belum diunggah) tanpa berarti orangnya
 * TIDAK LAGI di luar radius. Kartu lokasi harus tetap kuning selama radiusnya memang dilanggar,
 * terlepas dari pesan MANA yang sedang menang di rantai itu — jadi dihitung langsung dari
 * `classifyLocation`, sumber yang sama yang dipakai rantai itu sendiri (Gate: keduanya tidak
 * bisa saling menyimpang karena keduanya memanggil fungsi murni yang sama).
 *
 * Dipakai bersama PEMBACA (`CheckPage.tsx`) yang tahu kapan menahan `ReadinessNotice` supaya
 * pesan yang sama tidak tercetak DUA KALI — lihat komentar di sana untuk pembuktian bahwa
 * kedua kondisi (di sini dan di rantai prioritas) tidak pernah saling bertentangan.
 */
export function geoBoxState(
  draft: Pick<CheckInDraft, 'workMode' | 'location'>,
  policy: PolicyForCheckIn,
  nearest: NearestWorkLocation | null,
): GeoBoxState {
  const applies = geofenceAppliesTo(draft.workMode, policy);
  const verification = classifyLocation(draft.location, policy, { applies, nearest });
  if (verification !== 'OUTSIDE_GEOFENCE') {
    return { tone: 'neutral', message: null };
  }

  const lokasi = nearest
    ? `${nearest.name} (${Math.round(nearest.distanceMeters)} m)`
    : 'lokasi kerja';

  if (!policy.allowCheckInOutsideGeofence) {
    return {
      tone: 'warn',
      message: `Di luar radius ${lokasi} — kebijakan organisasi tidak mengizinkan absen masuk di luar radius.`,
    };
  }
  return {
    tone: 'warn',
    message: policy.outsideGeofenceRequiresReason
      ? `Di luar radius ${lokasi} — alasan wajib.`
      : `Di luar radius ${lokasi} — absen tetap tercatat beserta alasan bila diisi.`,
  };
}
